import { $createTextNode, $getState, $getStateChange, $setState, createState, EditorConfig, NodeKey, TextNode } from 'lexical';

const isEmptyState = createState('isEmpty', {
  parse: (value: boolean) => value ?? true,
});

const isActiveState = createState('isActive', {
  parse: (value: boolean) => value ?? false,
});

const processText = (text: string, isEmpty: boolean): [newText: string, newIsEmpty: boolean] => {
  if (text.trim().length < 1) return [' ', true];
  if (isEmpty) return [text.trim(), false];
  return [text, false];
};

export class ValueNode extends TextNode {
  static nodeType = 'search-value';

  constructor(text: string = '', key: NodeKey = null) {
    const [updatedText, isEmpty] = processText(text, true);
    super(updatedText, key);
    if (!isEmpty) $setState(this, isEmptyState, false);
  }

  $config() {
    return this.config(ValueNode.nodeType, {
      extends: TextNode,
      stateConfigs: [
        { flat: true, stateConfig: isEmptyState },
        { flat: true, stateConfig: isActiveState },
      ],
    });
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = super.createDOM(config);
    element.classList.add(ValueNode.nodeType);
    if ($getState(this, isEmptyState)) {
      element.classList.add('empty');
    }
    if ($getState(this, isActiveState)) {
      element.classList.add('active');
    }
    return element;
  }

  updateDOM(prevNode: this, element: HTMLElement, config: EditorConfig): boolean {
    if (super.updateDOM(prevNode, element, config)) {
      return true;
    }
    const emptyChange = $getStateChange(this, prevNode, isEmptyState);
    if (emptyChange !== null) {
      if (emptyChange[0]) {
        element.classList.add('empty');
      } else {
        element.classList.remove('empty');
      }
    }
    const activeChange = $getStateChange(this, prevNode, isActiveState);
    if (activeChange !== null) {
      if (activeChange[0]) {
        element.classList.add('active');
      } else {
        element.classList.remove('active');
      }
    }
    return false;
  }

  setTextContent(text: string): this {
    const wasEmpty = $getState(this, isEmptyState);
    const [updatedText, isEmpty] = processText(text, wasEmpty);
    if (wasEmpty !== isEmpty) $setState(this, isEmptyState, isEmpty);
    return super.setTextContent(updatedText);
  }

  isEmpty() {
    return $getState(this, isEmptyState);
  }

  setActive(isActive: boolean): this {
    const wasActive = $getState(this, isActiveState);
    if (wasActive !== isActive) {
      $setState(this, isActiveState, isActive);
    }
    return this;
  }

  isActive() {
    return $getState(this, isActiveState);
  }

  finalize() {
    const nextNode = this.getNextSibling();
    if (nextNode) {
      // it's better to avoid this clause, it doesn't work very well
      nextNode.selectStart();
    } else {
      const newNode = $createTextNode(' ');
      this.insertAfter(newNode);
      newNode.selectEnd();
    }
  }
}
